import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  LabelList
} from 'recharts';
import './PerformancePanel.css';
import { CategorySummary, PointEntry, ScrumNote, WeeklyTimeSummary } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_NAMES } from '../../config/teamConfig';

const API_URL = (process.env.REACT_APP_API_URL as string) || 'http://localhost:3001/api';

interface PerformancePanelProps {
  employeeId: number;
  employeeName: string;
  intranetEmployeeId?: string;
}

interface PointsByDate {
  date: string;
  category: string;
  total_points: number;
  count: number;
}

interface DailyTrend {
  date: string;
  points: number;
  cumulativePoints: number;
}

interface ScrumStats {
  total: number;
  open: number;
  resolved: number;
  bySeverity: { name: string; value: number; color: string }[];
}

interface IntranetDeveloper {
  id: string;
  name: string;
}

interface WorkTask {
  id: string;
  description: string;
  project: string;
  hours: number;
  type: string;
  status: string;
}

interface WorkTaskSummary {
  employeeId: string;
  startDate: string;
  endDate: string;
  days: number;
  totalHours: number;
  taskCount: number;
  tasks: WorkTask[];
}

interface AiModelOption {
  id: string;
  name: string;
}

interface WorkEvidenceTask {
  taskId: string;
  description: string;
  project: string;
  hours: number;
  reason: string;
}

interface EmployeeWorkAnalysis {
  summary?: string;
  overallAssessment?: string;
  detailedNarrative?: string;
  efficiencyScore?: number;
  reviewRiskScore?: number;
  timePaddingRiskScore?: number;
  confidence?: string;
  strengths?: string[];
  concerns?: string[];
  detailedFindings?: {
    title: string;
    detail: string;
    severity: 'positive' | 'neutral' | 'watch' | 'concern' | string;
  }[];
  patterns?: Record<string, string>;
  evidenceTasks?: WorkEvidenceTask[];
  estimateFindings?: {
    taskId: string;
    estimateHours: number;
    actualHours: number;
    varianceHours: number;
    variancePercent: number;
    interpretation: string;
  }[];
  managerQuestions?: string[];
  actionItems?: {
    issueType?: string;
    severity?: string;
    evidence?: string;
    managerStep?: string;
    developerStep?: string;
  }[];
  coachingRecommendations?: string[];
}

interface EmployeeWorkAnalysisResponse {
  model: string;
  analysis: EmployeeWorkAnalysis | null;
  raw: string | null;
}

interface EstimateMetricTask {
  id: number;
  intranetTaskId: string | null;
  taskDescription: string;
  projectName: string;
  status: string;
  estimateHours: number;
  actualHours: number;
  varianceHours: number;
  variancePercent: number;
  overEstimate: boolean;
}

interface EstimateMetrics {
  employeeId: string;
  taskCount: number;
  totalEstimatedHours: number;
  totalActualHours: number;
  totalVarianceHours: number;
  averageVariancePercent: number;
  overEstimateCount: number;
  severeOverrunCount: number;
  onOrUnderEstimateCount: number;
  onOrUnderEstimateRate: number;
  tasks: EstimateMetricTask[];
}

const CATEGORY_COLORS: Record<string, string> = {
  'Code Quality': '#4CAF50',
  'Code Standards': '#2196F3',
  'Deliverability': '#9C27B0',
  'Reliability': '#FF9800',
  'Problem Solving': '#00BCD4',
  'Initiative': '#E91E63',
  'Collaboration': '#673AB7',
  'Communication': '#3F51B5',
  'Professional Development': '#009688'
};

const SEVERITY_COLORS = {
  low: '#4CAF50',
  medium: '#FF9800',
  high: '#f44336'
};

const normalizeNameForMatching = (name: string): string =>
  name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getEditDistance = (a: string, b: string): number => {
  const matrix = Array.from({ length: a.length + 1 }, (_, row) =>
    Array.from({ length: b.length + 1 }, (_, col) => (row === 0 ? col : col === 0 ? row : 0))
  );

  for (let row = 1; row <= a.length; row++) {
    for (let col = 1; col <= b.length; col++) {
      const cost = a[row - 1] === b[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
};

const hasSimilarFirstName = (employeeFirstName: string, intranetFirstName: string): boolean => {
  if (!employeeFirstName || !intranetFirstName) return false;
  if (employeeFirstName[0] !== intranetFirstName[0]) return false;

  const distance = getEditDistance(employeeFirstName, intranetFirstName);
  return distance <= 1 || (Math.min(employeeFirstName.length, intranetFirstName.length) >= 5 && distance <= 2);
};

const PerformancePanel: React.FC<PerformancePanelProps> = ({ employeeId, employeeName, intranetEmployeeId }) => {
  const { user } = useAuth();
  const isManager = user?.username
    ? ADMIN_NAMES.includes(user.username.toLowerCase().trim())
    : false;
  const [categorySummary, setCategorySummary] = useState<CategorySummary[]>([]);
  const [, setPointsGrouped] = useState<PointsByDate[]>([]);
  const [allPoints, setAllPoints] = useState<PointEntry[]>([]);
  const [scrumNotes, setScrumNotes] = useState<ScrumNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [timeSummary, setTimeSummary] = useState<WeeklyTimeSummary | null>(null);
  const [timeDataLoading, setTimeDataLoading] = useState(false);
  const [timeDataError, setTimeDataError] = useState<string | null>(null);
  const [resolvedIntranetId, setResolvedIntranetId] = useState<string | null>(intranetEmployeeId || null);
  const [aiModels, setAiModels] = useState<AiModelOption[]>([]);
  const [selectedAiModel, setSelectedAiModel] = useState('');
  const [aiModelError, setAiModelError] = useState<string | null>(null);
  const [workAnalysisLoading, setWorkAnalysisLoading] = useState(false);
  const [workAnalysisError, setWorkAnalysisError] = useState<string | null>(null);
  const [workAnalysis, setWorkAnalysis] = useState<EmployeeWorkAnalysisResponse | null>(null);
  const [workTaskSummary, setWorkTaskSummary] = useState<WorkTaskSummary | null>(null);
  const [estimateMetrics, setEstimateMetrics] = useState<EstimateMetrics | null>(null);

  // Try to find intranet ID by matching employee name
  useEffect(() => {
    if (intranetEmployeeId) {
      setResolvedIntranetId(intranetEmployeeId);
      return;
    }

    const findIntranetId = async () => {
      setTimeDataLoading(true);
      setTimeDataError(null);
      try {
        const response = await axios.get<IntranetDeveloper[]>(`${API_URL}/intranet/developers`, {
          withCredentials: true
        });
        const developers = response.data;
        
        // Manual overrides for employees with name matching issues
        const NAME_TO_ID_OVERRIDES: Record<string, string> = {
          'anna ho': '249',
          'anna': '249',
          // Add more overrides as needed
        };
        
        // Try multiple matching strategies
        const normalizedName = normalizeNameForMatching(employeeName);
        
        // 0. Check for manual override first
        if (NAME_TO_ID_OVERRIDES[normalizedName]) {
          setResolvedIntranetId(NAME_TO_ID_OVERRIDES[normalizedName]);
          setTimeDataLoading(false);
          return;
        }
        const nameParts = normalizedName.split(' ').filter(p => p.length > 0);
        
        // 1. Exact match (full name)
        let match = developers.find(dev => 
          normalizeNameForMatching(dev.name) === normalizedName
        );
        
        // 2. First name + last name match (handles middle names)
        if (!match && nameParts.length >= 2) {
          const firstName = nameParts[0];
          const lastName = nameParts[nameParts.length - 1];
          match = developers.find(dev => {
            const devParts = normalizeNameForMatching(dev.name).split(' ');
            return devParts[0] === firstName && devParts[devParts.length - 1] === lastName;
          });
        }

        // 2b. Similar first name + exact last name (handles transliteration/typos like Danyil/Danylo)
        if (!match && nameParts.length >= 2) {
          const firstName = nameParts[0];
          const lastName = nameParts[nameParts.length - 1];
          const matchingLastNames = developers.filter(dev => {
            const devParts = normalizeNameForMatching(dev.name).split(' ');
            const devFirstName = devParts[0];
            const devLastName = devParts[devParts.length - 1];
            return devLastName === lastName && hasSimilarFirstName(firstName, devFirstName);
          });
          if (matchingLastNames.length === 1) {
            match = matchingLastNames[0];
          }
        }
        
        // 3. Check for unique first name match ONLY if there's no ambiguity
        // (e.g., don't match "Michael" if there are multiple Michaels)
        if (!match && nameParts.length >= 1) {
          const firstName = nameParts[0];
          const matchingFirstNames = developers.filter(dev => 
            normalizeNameForMatching(dev.name).split(' ')[0] === firstName
          );
          // Only use first name match if there's exactly one person with that first name
          if (matchingFirstNames.length === 1) {
            match = matchingFirstNames[0];
          }
        }
        
        // 4. Partial match - only if full name is contained (not first name only)
        // This prevents "Michael" from matching "Michael Beck" when we want "Michael Collins"
        if (!match && nameParts.length >= 2) {
          match = developers.find(dev => 
            normalizeNameForMatching(dev.name).includes(normalizedName) ||
            normalizedName.includes(normalizeNameForMatching(dev.name))
          );
        }
        
        // 5. Fuzzy match - check if first name starts with the same letters
        // This handles cases like "Anna" vs "Anna Ho" where database might have different format
        if (!match && nameParts.length >= 1) {
          const firstName = nameParts[0];
          match = developers.find(dev => {
            const devFirstName = normalizeNameForMatching(dev.name).split(' ')[0];
            // Match if first names are the same (case insensitive)
            return devFirstName === firstName;
          });
          // If multiple matches found with same first name, don't use this match
          const allMatches = developers.filter(dev => {
            const devFirstName = normalizeNameForMatching(dev.name).split(' ')[0];
            return devFirstName === firstName;
          });
          if (allMatches.length > 1) {
            match = undefined;
          }
        }
        
        if (match) {
          setResolvedIntranetId(match.id);
        } else {
          console.debug(`[PerformancePanel] Could not match "${employeeName}" to any developer. Available:`, 
            developers.map(d => d.name).join(', '));
          setTimeDataError(`Could not find "${employeeName}" in intranet developers list`);
        }
      } catch (err) {
        console.debug('Could not fetch intranet developers:', err);
        setTimeDataError('Could not connect to intranet. Make sure you are on VPN and logged in.');
      } finally {
        setTimeDataLoading(false);
      }
    };

    findIntranetId();
  }, [employeeName, intranetEmployeeId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, groupedRes, pointsRes, scrumRes] = await Promise.all([
        axios.get<CategorySummary[]>(`${API_URL}/points/employee/${employeeId}/summary`),
        axios.get<PointsByDate[]>(`${API_URL}/points/employee/${employeeId}/grouped`),
        axios.get<PointEntry[]>(`${API_URL}/points/employee/${employeeId}`),
        axios.get<ScrumNote[]>(`${API_URL}/scrum-notes/employee/${employeeId}`)
      ]);
      setCategorySummary(summaryRes.data);
      setPointsGrouped(groupedRes.data);
      setAllPoints(pointsRes.data);
      setScrumNotes(scrumRes.data);
    } catch (err) {
      console.error('Error fetching performance data:', err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  // Fetch time summary when we have an intranet ID
  useEffect(() => {
    if (!resolvedIntranetId) return;

    const fetchTimeSummary = async () => {
      setTimeDataLoading(true);
      try {
        const response = await axios.get<WeeklyTimeSummary>(
          `${API_URL}/intranet/time-summary/${resolvedIntranetId}`,
          { withCredentials: true }
        );
        setTimeSummary(response.data);
        setTimeDataError(null);
      } catch (err) {
        console.debug('Could not fetch time summary:', err);
        setTimeSummary(null);
        setTimeDataError('Could not fetch time data from intranet');
      } finally {
        setTimeDataLoading(false);
      }
    };

    fetchTimeSummary();
  }, [resolvedIntranetId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!isManager) return;

    const fetchAiModels = async () => {
      try {
        const response = await axios.get<{ configured: boolean; models: AiModelOption[] }>(
          `${API_URL}/ai/models`,
          { withCredentials: true }
        );
        setAiModels(response.data.models || []);
        setSelectedAiModel(current => current || response.data.models?.[0]?.id || '');
        if (!response.data.configured) {
          setAiModelError('OpenRouter is not configured.');
        }
      } catch (err: any) {
        setAiModelError(err.response?.data?.error || 'Failed to load AI models');
      }
    };

    fetchAiModels();
  }, [isManager]);

  const handleAnalyzeRecentWork = useCallback(async () => {
    if (!isManager || !resolvedIntranetId || !selectedAiModel) return;

    setWorkAnalysisLoading(true);
    setWorkAnalysisError(null);
    setWorkAnalysis(null);

    try {
      const taskResponse = await axios.get<WorkTaskSummary>(
        `${API_URL}/intranet/tasks/${resolvedIntranetId}/range?days=30`,
        { withCredentials: true }
      );
      setWorkTaskSummary(taskResponse.data);

      const estimateMetricsResponse = await axios.get<EstimateMetrics>(
        `${API_URL}/task-estimates/employee/${resolvedIntranetId}/metrics`,
        { withCredentials: true }
      );
      setEstimateMetrics(estimateMetricsResponse.data);

      const analysisResponse = await axios.post<EmployeeWorkAnalysisResponse>(
        `${API_URL}/ai/employee-work-analysis`,
        {
          model: selectedAiModel,
          employeeName,
          intranetEmployeeId: resolvedIntranetId,
          taskSummary: taskResponse.data,
          estimateMetrics: estimateMetricsResponse.data
        },
        { withCredentials: true }
      );

      setWorkAnalysis(analysisResponse.data);
    } catch (err: any) {
      setWorkAnalysisError(err.response?.data?.error || 'Failed to analyze recent work');
    } finally {
      setWorkAnalysisLoading(false);
    }
  }, [employeeName, isManager, resolvedIntranetId, selectedAiModel]);

  // Filter data by date range
  const filteredPoints = useMemo(() => {
    if (dateRange === 'all') return allPoints;
    
    const now = new Date();
    const daysMap = { '7d': 7, '30d': 30, '90d': 90, 'all': Infinity };
    const cutoff = new Date(now.getTime() - daysMap[dateRange] * 24 * 60 * 60 * 1000);
    
    return allPoints.filter(p => new Date(p.date) >= cutoff);
  }, [allPoints, dateRange]);

  // Radar chart data for category breakdown
  const radarData = useMemo(() => {
    return categorySummary.map(cat => ({
      category: cat.category.replace('Professional Development', 'Prof. Dev'),
      points: cat.total_points,
      fullMark: Math.max(...categorySummary.map(c => c.total_points), 10)
    }));
  }, [categorySummary]);

  // Daily trend data
  const dailyTrend = useMemo((): DailyTrend[] => {
    const pointsByDay = new Map<string, number>();
    
    filteredPoints.forEach(p => {
      const existing = pointsByDay.get(p.date) || 0;
      pointsByDay.set(p.date, existing + p.points);
    });

    const sorted = Array.from(pointsByDay.entries())
      .sort(([a], [b]) => a.localeCompare(b));

    let cumulative = 0;
    return sorted.map(([date, points]) => {
      cumulative += points;
      return {
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        points,
        cumulativePoints: cumulative
      };
    });
  }, [filteredPoints]);

  // Category bar chart data
  const categoryBarData = useMemo(() => {
    return categorySummary
      .sort((a, b) => b.total_points - a.total_points)
      .map(cat => ({
        name: cat.category.length > 12 ? cat.category.substring(0, 12) + '...' : cat.category,
        fullName: cat.category,
        points: cat.total_points,
        entries: cat.count,
        fill: CATEGORY_COLORS[cat.category] || '#888'
      }));
  }, [categorySummary]);

  // Scrum statistics
  const scrumStats = useMemo((): ScrumStats => {
    const open = scrumNotes.filter(n => n.status === 'open').length;
    const resolved = scrumNotes.filter(n => n.status === 'resolved').length;
    
    const severityCounts = { low: 0, medium: 0, high: 0 };
    scrumNotes.forEach(n => {
      if (n.status === 'open') {
        severityCounts[n.severity]++;
      }
    });

    return {
      total: scrumNotes.length,
      open,
      resolved,
      bySeverity: [
        { name: 'Low', value: severityCounts.low, color: SEVERITY_COLORS.low },
        { name: 'Medium', value: severityCounts.medium, color: SEVERITY_COLORS.medium },
        { name: 'High', value: severityCounts.high, color: SEVERITY_COLORS.high }
      ].filter(s => s.value > 0)
    };
  }, [scrumNotes]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    const totalPoints = categorySummary.reduce((sum, c) => sum + c.total_points, 0);
    const totalEntries = categorySummary.reduce((sum, c) => sum + c.count, 0);
    const avgPerEntry = totalEntries > 0 ? (totalPoints / totalEntries).toFixed(1) : '0';
    const topCategory = categorySummary.length > 0 
      ? categorySummary.reduce((max, c) => c.total_points > max.total_points ? c : max, categorySummary[0])
      : null;
    const lowestCategory = categorySummary.length > 0
      ? categorySummary.reduce((min, c) => c.total_points < min.total_points ? c : min, categorySummary[0])
      : null;

    return {
      totalPoints,
      totalEntries,
      avgPerEntry,
      topCategory: topCategory?.category || 'N/A',
      lowestCategory: lowestCategory?.category || 'N/A',
      resolveRate: scrumStats.total > 0 
        ? Math.round((scrumStats.resolved / scrumStats.total) * 100) 
        : 100
    };
  }, [categorySummary, scrumStats]);

  if (loading) {
    return <div className="performance-loading">Loading performance data...</div>;
  }

  const hasTrackedFeedback = categorySummary.length > 0 || scrumNotes.length > 0;
  const hasIntranetStatus = Boolean(resolvedIntranetId || timeSummary || timeDataLoading || timeDataError);
  const shouldShowPanel = hasTrackedFeedback || hasIntranetStatus || isManager;

  if (!shouldShowPanel) {
    return (
      <div className="performance-empty">
        <div className="empty-icon">📊</div>
        <h3>No Performance Data Yet</h3>
        <p>Start tracking points and scrum notes to see {employeeName}'s performance charts here.</p>
      </div>
    );
  }

  const renderAnalysisList = (title: string, items?: string[]) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="work-ai-section">
        <span className="work-ai-section-title">{title}</span>
        <ul>
          {items.map((item, index) => (
            <li key={`${title}-${index}`}>{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  const formatScore = (score: number): string => {
    return Number(score).toFixed(1);
  };

  const formatAiLabel = (value: string): string => {
    return value
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const getFindingScore = (severity: string): number => {
    const normalized = String(severity || '').toLowerCase();
    if (normalized === 'positive') return 25;
    if (normalized === 'neutral') return 45;
    if (normalized === 'watch') return 70;
    if (normalized === 'concern') return 90;
    return 50;
  };

  const getFindingColor = (severity: string): string => {
    const normalized = String(severity || '').toLowerCase();
    if (normalized === 'positive') return '#4CAF50';
    if (normalized === 'watch') return '#F4B41A';
    if (normalized === 'concern') return '#f44336';
    return '#9ca3af';
  };

  const renderFormattedAiText = (text: string) => {
    const regex = /('?\[[^\]]+\][^()'\n]{0,180}?\(\d*\.?\d+\s*hours?\)'?|'\[[^\]]+\][^()'\n]{0,180}?\(\d*\.?\d+\s*hrs?\.?\)'?|(?<![A-Za-z])'[^'\n]{8,180}'(?=\s*\(\d*\.?\d+\s*(?:hours?|hrs?\.?)\))|\b\d*\.?\d+\s*(?:hours?|hrs?\.?)\b|#[0-9]+)/gi;
    const parts = text.split(regex).filter(Boolean);

    return parts.map((part, index) => {
      const cleanedTask = part.replace(/^'|'$/g, '');
      if (/^\[[^\]]+\]/.test(cleanedTask)) {
        const hoursMatch = cleanedTask.match(/\(?(\d*\.?\d+\s*(?:hours?|hrs?\.?))\)?$/i);
        const withoutHours = cleanedTask.replace(/\s*\(\d*\.?\d+\s*(?:hours?|hrs?\.?)\)\s*$/i, '');
        const projectMatch = withoutHours.match(/^(\[[^\]]+\])\s*(.*)$/);
        return (
          <span key={index} className="work-ai-inline-task">
            <span className="work-ai-inline-task-project">{projectMatch?.[1]}</span>
            <span className="work-ai-inline-task-title">{projectMatch?.[2] || withoutHours}</span>
            {hoursMatch && <span className="work-ai-inline-task-hours">{hoursMatch[1]}</span>}
          </span>
        );
      }
      if (/^'[^']+'$/.test(part)) {
        return <span key={index} className="work-ai-inline-description">{part.slice(1, -1)}</span>;
      }
      if (/^\b\d*\.?\d+\s*(?:hours?|hrs?\.?)\b/i.test(part)) {
        return <span key={index} className="work-ai-inline-hours">{part}</span>;
      }
      if (/^#[0-9]+/.test(part)) {
        return <span key={index} className="work-ai-inline-task-id">{part}</span>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const renderWorkAnalysis = () => {
    if (workAnalysisLoading) {
      return <div className="work-ai-result work-ai-loading">Analyzing last 30 days of work...</div>;
    }

    if (workAnalysisError) {
      return <div className="work-ai-result error">{workAnalysisError}</div>;
    }

    if (!workAnalysis) return null;

    const analysis = workAnalysis.analysis;
    if (!analysis) {
      return <div className="work-ai-result">{workAnalysis.raw || 'No analysis returned.'}</div>;
    }

    return (
      <div className="work-ai-result">
        <div className="work-ai-score-row">
          {analysis.efficiencyScore !== undefined && (
            <span>Efficiency <strong>{formatScore(analysis.efficiencyScore)}/100</strong></span>
          )}
          {analysis.reviewRiskScore !== undefined && (
            <span>Review risk <strong>{formatScore(analysis.reviewRiskScore)}/100</strong></span>
          )}
          {analysis.timePaddingRiskScore !== undefined && (
            <span>Padding risk <strong>{formatScore(analysis.timePaddingRiskScore)}/100</strong></span>
          )}
        </div>

        {analysis.summary && <p className="work-ai-summary">{analysis.summary}</p>}
        {analysis.detailedNarrative && (
          <div className="work-ai-section">
            <span className="work-ai-section-title">Detailed readout</span>
            {analysis.detailedNarrative.split(/\n{2,}/).map((paragraph, index) => (
              <p key={index}>{renderFormattedAiText(paragraph)}</p>
            ))}
          </div>
        )}

        {analysis.patterns && (
          <div className="work-ai-section">
            <span className="work-ai-section-title">Work patterns</span>
            <div className="work-ai-pattern-grid">
              {Object.entries(analysis.patterns).map(([key, value]) => (
                <div key={key}>
                  <strong>{formatAiLabel(key)}</strong>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {analysis.detailedFindings && analysis.detailedFindings.length > 0 && (
          <div className="work-ai-section">
            <span className="work-ai-section-title">Detailed findings</span>
            <div className="work-ai-findings-chart">
              <ResponsiveContainer width="100%" height={Math.max(180, analysis.detailedFindings.length * 52)}>
                <BarChart
                  data={analysis.detailedFindings.map((finding) => ({
                    ...finding,
                    label: finding.title,
                    score: getFindingScore(finding.severity || 'neutral')
                  }))}
                  layout="vertical"
                  margin={{ top: 4, right: 64, left: 10, bottom: 4 }}
                >
                  <XAxis type="number" domain={[0, 100]} hide />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={180}
                    tick={{ fill: '#2C3E35', fontSize: 12, fontWeight: 700 }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => [
                      formatAiLabel(props.payload.severity || 'neutral'),
                      'Severity'
                    ]}
                    labelFormatter={(label) => label}
                    contentStyle={{
                      background: '#2C3E35',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="score" radius={[0, 8, 8, 0]}>
                    {analysis.detailedFindings.map((finding, index) => (
                      <Cell key={`finding-${index}`} fill={getFindingColor(finding.severity || 'neutral')} />
                    ))}
                    <LabelList
                      dataKey="severity"
                      position="right"
                      formatter={(value) => formatAiLabel(String(value || 'neutral'))}
                      fill="#2C3E35"
                      fontSize={11}
                      fontWeight={700}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="work-ai-findings-details">
              {analysis.detailedFindings.map((finding, index) => (
                <p key={index}>
                  <strong>{finding.title}:</strong> {renderFormattedAiText(finding.detail)}
                </p>
              ))}
            </div>
          </div>
        )}

        {renderAnalysisList('Strengths', analysis.strengths)}
        {renderAnalysisList('Concerns', analysis.concerns)}

        {analysis.evidenceTasks && analysis.evidenceTasks.length > 0 && (
          <div className="work-ai-section">
            <span className="work-ai-section-title">Task evidence</span>
            {analysis.evidenceTasks.map((task, index) => (
              <div key={`${task.taskId}-${index}`} className="work-ai-evidence-task">
                <strong>#{task.taskId} · {task.project} · {Number(task.hours || 0).toFixed(2)}h</strong>
                <p>{task.description}</p>
                <span>{task.reason}</span>
              </div>
            ))}
          </div>
        )}

        {analysis.estimateFindings && analysis.estimateFindings.length > 0 && (
          <div className="work-ai-section">
            <span className="work-ai-section-title">Estimate findings</span>
            {analysis.estimateFindings.map((finding, index) => (
              <div key={`${finding.taskId}-${index}`} className="work-ai-evidence-task">
                <strong>
                  #{finding.taskId} · estimate {Number(finding.estimateHours || 0).toFixed(1)}h · actual {Number(finding.actualHours || 0).toFixed(1)}h · variance {Number(finding.varianceHours || 0).toFixed(1)}h
                </strong>
                <span>{finding.interpretation}</span>
              </div>
            ))}
          </div>
        )}

        {renderAnalysisList('Manager questions', analysis.managerQuestions)}
        {analysis.actionItems && analysis.actionItems.length > 0 && (
          <div className="work-ai-section">
            <span className="work-ai-section-title">Action items</span>
            {analysis.actionItems.map((item, index) => (
              <div key={`action-${index}`} className="work-ai-evidence-task">
                <strong>
                  {formatAiLabel(item.issueType || 'review')} · {formatAiLabel(item.severity || 'medium')}
                </strong>
                <p>{item.evidence}</p>
                <span><strong>Manager:</strong> {item.managerStep}</span>
                <span><strong>Developer:</strong> {item.developerStep}</span>
              </div>
            ))}
          </div>
        )}
        {renderAnalysisList('Coaching recommendations', analysis.coachingRecommendations)}
      </div>
    );
  };

  return (
    <div className="performance-panel">
      {hasTrackedFeedback ? (
        <>
          {/* Date Range Filter */}
          <div className="performance-filters">
            <span className="filter-label">Time Period:</span>
            <div className="filter-buttons">
              {(['7d', '30d', '90d', 'all'] as const).map(range => (
                <button
                  key={range}
                  className={`filter-btn ${dateRange === range ? 'active' : ''}`}
                  onClick={() => setDateRange(range)}
                >
                  {range === 'all' ? 'All Time' : range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="performance-summary-grid">
            <div className="perf-stat-card">
              <span className="perf-stat-value">{summaryStats.totalPoints}</span>
              <span className="perf-stat-label">Total Points</span>
            </div>
            <div className="perf-stat-card">
              <span className="perf-stat-value">{summaryStats.totalEntries}</span>
              <span className="perf-stat-label">Point Entries</span>
            </div>
            <div className="perf-stat-card">
              <span className="perf-stat-value">{summaryStats.avgPerEntry}</span>
              <span className="perf-stat-label">Avg per Entry</span>
            </div>
            <div className="perf-stat-card">
              <span className="perf-stat-value">{summaryStats.resolveRate}%</span>
              <span className="perf-stat-label">Issue Resolve Rate</span>
            </div>
          </div>
        </>
      ) : (
        <div className="performance-data-note">
          <div className="empty-icon">📊</div>
          <div>
            <h3>No scored feedback yet</h3>
            <p>
              {employeeName} does not have local point entries or scrum notes yet. Intranet time utilization
              and manager analysis can still load below when the intranet employee is matched.
            </p>
          </div>
        </div>
      )}

      {isManager && (
        <div className="work-ai-card">
          <div className="work-ai-header">
            <div>
              <h4>AI Work Analysis</h4>
              <p>Concrete review of {employeeName}'s last 30 days of tracked tasks.</p>
              {workTaskSummary && (
                <span className="work-ai-range">
                  {workTaskSummary.taskCount} tasks · {workTaskSummary.totalHours.toFixed(2)}h · {workTaskSummary.startDate} to {workTaskSummary.endDate}
                </span>
              )}
            </div>
            <div className="work-ai-controls">
              <select
                value={selectedAiModel}
                onChange={(event) => setSelectedAiModel(event.target.value)}
                disabled={aiModels.length === 0}
              >
                {aiModels.length === 0 ? (
                  <option value="">No AI models</option>
                ) : (
                  aiModels.map(model => (
                    <option key={model.id} value={model.id}>{model.name}</option>
                  ))
                )}
              </select>
              <button
                type="button"
                onClick={handleAnalyzeRecentWork}
                disabled={!resolvedIntranetId || !selectedAiModel || Boolean(aiModelError) || workAnalysisLoading}
              >
                {workAnalysisLoading ? 'Analyzing...' : 'Analyze Last 30 Days'}
              </button>
            </div>
          </div>
          {aiModelError && <div className="work-ai-error-note">{aiModelError}</div>}
          {!resolvedIntranetId && <div className="work-ai-error-note">No intranet employee ID found for time analysis.</div>}
        {estimateMetrics && estimateMetrics.taskCount > 0 && (
          <div className="estimate-metrics-strip">
            <div>
              <span>Estimated vs actual</span>
              <strong>{estimateMetrics.totalEstimatedHours.toFixed(1)}h / {estimateMetrics.totalActualHours.toFixed(1)}h</strong>
            </div>
            <div>
              <span>Over estimate</span>
              <strong>{estimateMetrics.overEstimateCount}/{estimateMetrics.taskCount}</strong>
            </div>
            <div>
              <span>Severe overruns</span>
              <strong>{estimateMetrics.severeOverrunCount}</strong>
            </div>
            <div>
              <span>On/under rate</span>
              <strong>{estimateMetrics.onOrUnderEstimateRate.toFixed(1)}%</strong>
            </div>
          </div>
        )}
          {renderWorkAnalysis()}
        </div>
      )}

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Category Radar Chart */}
        {radarData.length > 0 && (
          <div className="chart-card">
            <h4>Category Strengths</h4>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e0e0e0" />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ fill: '#2C3E35', fontSize: 11 }}
                  />
                  <PolarRadiusAxis 
                    angle={30} 
                    domain={[0, 'auto']}
                    tick={{ fill: '#666', fontSize: 10 }}
                  />
                  <Radar
                    name="Points"
                    dataKey="points"
                    stroke="#F4B41A"
                    fill="#F4B41A"
                    fillOpacity={0.5}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#2C3E35', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Category Bar Chart */}
        {categoryBarData.length > 0 && (
          <div className="chart-card">
            <h4>Points by Category</h4>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryBarData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" tick={{ fill: '#666', fontSize: 11 }} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={100}
                    tick={{ fill: '#2C3E35', fontSize: 11 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#2C3E35', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Bar dataKey="points" radius={[0, 4, 4, 0]}>
                    {categoryBarData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Points Trend Line Chart */}
        {dailyTrend.length > 1 && (
          <div className="chart-card chart-wide">
            <h4>Points Trend Over Time</h4>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#666', fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fill: '#666', fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#2C3E35', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="points" 
                    name="Daily Points"
                    stroke="#F4B41A" 
                    strokeWidth={2}
                    dot={{ fill: '#F4B41A', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativePoints" 
                    name="Cumulative"
                    stroke="#2C3E35" 
                    strokeWidth={2}
                    dot={{ fill: '#2C3E35', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Scrum Issues Pie Chart */}
        {scrumStats.total > 0 && (
          <div className="chart-card">
            <h4>Scrum Issue Status</h4>
            <div className="chart-container scrum-chart">
              {/* Main Open vs Resolved Pie Chart */}
              <div className="status-chart-container">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Open', value: scrumStats.open, color: '#F4B41A' },
                        { name: 'Resolved', value: scrumStats.resolved, color: '#4CAF50' }
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={scrumStats.open > 0 && scrumStats.resolved > 0 ? 3 : 0}
                      dataKey="value"
                      label={({ name, value, percent }) => `${name}: ${value} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: '#666' }}
                    >
                      {[
                        { name: 'Open', value: scrumStats.open, color: '#F4B41A' },
                        { name: 'Resolved', value: scrumStats.resolved, color: '#4CAF50' }
                      ].filter(d => d.value > 0).map((entry, index) => (
                        <Cell key={`status-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        background: '#2C3E35', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: number, name: string) => [`${value} issues`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="chart-center-label">
                  <span className="total-value">{scrumStats.total}</span>
                  <span className="total-label">Total</span>
                </div>
              </div>

              {/* Severity Breakdown - shown as small badges below */}
              {scrumStats.open > 0 && scrumStats.bySeverity.length > 0 && (
                <div className="severity-breakdown">
                  <h5 className="severity-title">Open by Severity</h5>
                  <div className="severity-badges">
                    {scrumStats.bySeverity.map((sev, idx) => (
                      <div key={idx} className="severity-badge" style={{ borderColor: sev.color }}>
                        <span className="severity-count" style={{ color: sev.color }}>{sev.value}</span>
                        <span className="severity-name">{sev.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scrumStats.open === 0 && (
                <div className="all-resolved">
                  <span className="checkmark">✓</span>
                  <p>All issues resolved!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Time Utilization Chart - side by side with Scrum chart */}
        {timeDataLoading && (
          <div className="chart-card">
            <h4>Weekly Time Utilization</h4>
            <div className="time-loading">
              <span>Loading time data from intranet...</span>
            </div>
          </div>
        )}

        {!timeDataLoading && timeDataError && (
          <div className="chart-card">
            <h4>Weekly Time Utilization</h4>
            <div className="time-error">
              <span className="error-icon">⚠️</span>
              <p>{timeDataError}</p>
              <p className="error-hint">Time tracking data requires VPN connection and intranet login.</p>
            </div>
          </div>
        )}

        {!timeDataLoading && timeSummary && (
          <div className="chart-card">
            <h4>Weekly Time Utilization</h4>
            <div className="time-utilization-content">
              {/* Task Running Status */}
              {(() => {
                // Find if there's any running task
                const runningTask = Object.values(timeSummary.tasksByDay)
                  .flatMap(day => day.taskList || [])
                  .find(task => task.isRunning);
                
                return (
                  <div className="task-status-row">
                    <div className="task-status-info">
                      <span className={`task-status ${runningTask ? 'task-running' : 'task-idle'}`}>
                        {runningTask ? '● Task Running' : '○ No Active Task'}
                      </span>
                      {runningTask && (
                        <span className="task-description">{runningTask.project}: {runningTask.description}</span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Main Stats and Progress Bar */}
              {(() => {
                // Only show warning/behind status after 6:30 PM EST on a weekday
                // Before that, they still have time to complete their hours
                const now = new Date();
                const estOffset = -5; // EST is UTC-5 (ignoring DST for simplicity)
                const utcHours = now.getUTCHours();
                const estHours = (utcHours + estOffset + 24) % 24;
                const estMinutes = now.getUTCMinutes();
                const isAfter630PM = estHours > 18 || (estHours === 18 && estMinutes >= 30);
                const dayOfWeek = now.getDay(); // 0=Sun, 6=Sat
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                
                // Can show warnings only after 6:30 PM EST or on weekends
                const canShowWarnings = isAfter630PM || isWeekend;
                
                // Helper to get color class for metrics
                const getDailyAvgClass = () => {
                  if (timeSummary.dailyAverage >= 7) return 'good';
                  if (!canShowWarnings) return ''; // No warning color during the day
                  return timeSummary.dailyAverage >= 5 ? 'warning' : 'low';
                };
                
                const getUtilizationClass = () => {
                  if (timeSummary.utilizationPercent >= 90) return 'good';
                  if (!canShowWarnings) return ''; // No warning color during the day
                  return timeSummary.utilizationPercent >= 70 ? 'warning' : 'low';
                };
                
                return (
                  <>
                    {/* Main Stats - Compact 3-column grid */}
                    <div className="time-stats-row">
                      <div className="time-stat-item primary">
                        <span className="time-stat-value">{timeSummary.taskHoursTotal.toFixed(1)}h</span>
                        <span className="time-stat-label">Task Time</span>
                        <span className="time-stat-sub">of {timeSummary.expectedHoursSoFar}h expected</span>
                      </div>
                      <div className="time-stat-item">
                        <span className={`time-stat-value ${getDailyAvgClass()}`}>
                          {timeSummary.dailyAverage.toFixed(1)}h
                        </span>
                        <span className="time-stat-label">Daily Avg</span>
                        <span className="time-stat-sub">Target: 7h</span>
                      </div>
                      <div className="time-stat-item">
                        <span className={`time-stat-value ${getUtilizationClass()}`}>
                          {timeSummary.utilizationPercent}%
                        </span>
                        <span className="time-stat-label">On Track</span>
                      </div>
                    </div>
                  </>
                );
              })()}
              
              {/* Progress Bar */}
              {(() => {
                // Only show "Behind" status after 6:30 PM EST on a weekday
                // Before that, they still have time to complete their hours
                const now = new Date();
                const estOffset = -5; // EST is UTC-5 (ignoring DST for simplicity)
                const utcHours = now.getUTCHours();
                const estHours = (utcHours + estOffset + 24) % 24;
                const estMinutes = now.getUTCMinutes();
                const isAfter630PM = estHours > 18 || (estHours === 18 && estMinutes >= 30);
                const dayOfWeek = now.getDay(); // 0=Sun, 6=Sat
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                
                // Show behind status only if:
                // 1. It's after 6:30 PM EST on a weekday, OR
                // 2. It's the weekend (previous days should be complete)
                // 3. AND they're actually behind (< 90%)
                const canShowBehind = (isAfter630PM || isWeekend) && timeSummary.utilizationPercent < 90;
                const isOnTrack = timeSummary.utilizationPercent >= 90;
                
                // Determine status text and style
                let statusText = '✓ On Track';
                let statusClass = 'status-good';
                let barClass = 'good';
                
                if (isOnTrack) {
                  statusText = '✓ On Track';
                  statusClass = 'status-good';
                  barClass = 'good';
                } else if (canShowBehind) {
                  statusText = '⚠ Behind';
                  statusClass = timeSummary.utilizationPercent >= 70 ? 'status-warning' : 'status-low';
                  barClass = timeSummary.utilizationPercent >= 70 ? 'warning' : 'low';
                } else {
                  // During the day, show neutral "In Progress" status
                  statusText = '◐ In Progress';
                  statusClass = 'status-neutral';
                  barClass = 'neutral';
                }
                
                return (
                  <div className="utilization-bar-container">
                    <div className="utilization-bar-header">
                      <span>{timeSummary.workdaysPassed} day{timeSummary.workdaysPassed !== 1 ? 's' : ''}</span>
                      <span className={statusClass}>{statusText}</span>
                    </div>
                    <div className="utilization-bar-bg">
                      <div 
                        className={`utilization-bar-fill ${barClass}`}
                        style={{ width: `${Math.min(timeSummary.utilizationPercent, 100)}%` }}
                      />
                      {timeSummary.utilizationPercent > 100 && (
                        <div 
                          className="utilization-bar-overflow"
                          style={{ width: `${Math.min(timeSummary.utilizationPercent - 100, 50)}%` }}
                        />
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Daily Breakdown - Show all 7 days of the week */}
              <div className="daily-breakdown">
                <h5>Daily Breakdown</h5>
                <div className="daily-bars">
                  {(() => {
                    // Generate all 7 days (Mon-Sun) for the current week
                    const weekStart = new Date(timeSummary.weekStart);
                    const today = new Date();
                    today.setHours(12, 0, 0, 0); // Normalize to noon
                    
                    const allDays = [];
                    for (let i = 0; i < 7; i++) {
                      const day = new Date(weekStart);
                      day.setDate(weekStart.getDate() + i);
                      day.setHours(12, 0, 0, 0);
                      const dateStr = `${(day.getMonth() + 1).toString().padStart(2, '0')}/${day.getDate().toString().padStart(2, '0')}/${day.getFullYear()}`;
                      const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
                      const isToday = day.toDateString() === today.toDateString();
                      const isFuture = day > today;
                      const dayOfWeek = day.getDay(); // 0=Sun, 6=Sat
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                      allDays.push({ dateStr, dayName, day, isToday, isFuture, isWeekend });
                    }
                    
                    const formatTime = (seconds: number) => {
                      const h = Math.floor(seconds / 3600);
                      const m = Math.floor((seconds % 3600) / 60);
                      return h > 0 ? `${h}h ${m}m` : `${m}m`;
                    };
                    
                    return allDays.map(({ dateStr, dayName, isToday, isFuture, isWeekend }) => {
                      const data = timeSummary.tasksByDay[dateStr];
                      const hours = data ? data.timeSeconds / 3600 : 0;
                      const heightPercent = hours > 0 ? Math.min((hours / 8) * 100, 100) : 0;
                      const hasData = data && data.timeSeconds > 0;
                      
                      return (
                        <div 
                          key={dateStr} 
                          className={`daily-bar-item ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''} ${isWeekend ? 'weekend' : ''} ${!hasData ? 'no-data' : ''}`}
                        >
                          <div className="daily-bar-wrapper">
                            {hasData ? (
                              <>
                                <div 
                                  className={`daily-bar ${hours >= 7 ? 'good' : hours >= 4 ? 'warning' : 'low'}`}
                                  style={{ height: `${heightPercent}%` }}
                                />
                                {/* Tooltip */}
                                <div className="daily-bar-tooltip">
                                  <div className="tooltip-header">
                                    <strong>{dayName}</strong> - {hours.toFixed(2)} hours ({data.tasks} tasks)
                                  </div>
                                  <div className="tooltip-tasks">
                                    {data.taskList && data.taskList.length > 0 ? (
                                      data.taskList.slice(0, 10).map((task, idx) => (
                                        <div key={idx} className={`tooltip-task ${task.isRunning ? 'running' : ''}`}>
                                          <span className="tooltip-task-time">
                                            {task.timeHours || formatTime(task.timeSeconds)} hrs
                                          </span>
                                          <span className={`tooltip-task-type type-${task.type?.toLowerCase()}`}>
                                            {task.type}
                                          </span>
                                          <span className="tooltip-task-project" title={task.project}>
                                            {task.project.length > 15 ? task.project.substring(0, 15) + '...' : task.project}
                                          </span>
                                          <span className="tooltip-task-desc" title={task.description}>
                                            {task.description.length > 35 
                                              ? task.description.substring(0, 35) + '...' 
                                              : task.description}
                                            {task.isRunning && <span className="running-badge">▶ RUNNING</span>}
                                          </span>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="tooltip-empty">No task details available</div>
                                    )}
                                    {data.taskList && data.taskList.length > 10 && (
                                      <div className="tooltip-more">+{data.taskList.length - 10} more tasks</div>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className={`daily-bar-empty ${isFuture ? 'future' : ''} ${isWeekend ? 'weekend' : ''}`}>
                                {isFuture || isWeekend ? '' : '—'}
                              </div>
                            )}
                          </div>
                          <span className="daily-bar-hours">{hasData ? `${hours.toFixed(1)}h` : (isFuture || isWeekend ? '' : '0h')}</span>
                          <span className={`daily-bar-date ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}>{dayName}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Only show behind warning after 6:30 PM EST or on weekends */}
              {(() => {
                const now = new Date();
                const estOffset = -5;
                const utcHours = now.getUTCHours();
                const estHours = (utcHours + estOffset + 24) % 24;
                const estMinutes = now.getUTCMinutes();
                const isAfter630PM = estHours > 18 || (estHours === 18 && estMinutes >= 30);
                const dayOfWeek = now.getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const canShowWarning = isAfter630PM || isWeekend;
                
                if (!canShowWarning || timeSummary.utilizationPercent >= 70 || timeSummary.workdaysPassed === 0) {
                  return null;
                }
                
                return (
                  <div className="utilization-warning">
                    ⚠️ Currently {(timeSummary.expectedHoursSoFar - timeSummary.taskHoursTotal).toFixed(1)}h behind expected. 
                    {timeSummary.dailyAverage < 5 
                      ? ' Daily average is low - ensure all work is being tracked.'
                      : ' Keep logging tasks to catch up.'}
                  </div>
                );
              })()}
              {timeSummary.utilizationPercent >= 100 && (
                <div className="utilization-success">
                  ✅ Exceeding expectations! {timeSummary.taskHoursTotal.toFixed(1)}h logged vs {timeSummary.expectedHoursSoFar}h expected.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformancePanel;

